import React from "react";
import { FieldHelp, LookupActions, lookupEntryNoun, lookupNoun, lookupSelectProps, singularNoun, useCanAddLookup } from "../LookupMenu";
import classes from "./record.module.scss";

interface SelectFieldProps {
    /** Field name — the notched label once it has a value (the select's placeholder says it while empty). */
    label: string;
    /** Whether the select holds a value. Defaults to reading the child select's `value`. */
    hasValue?: boolean;
    required?: boolean;
    /** Help behind the gold ⓘ in the notched label (or the placeholder while empty). */
    tooltip?: string;
    /** Lookup pickers: "Reload list" in the open list's footer… */
    onRefresh?: () => void;
    /** …and "Add new <thing>", which opens the lookup's create page in a new tab. */
    adminPageUrl?: string;
    /** Explicit create route for irregular / nested lookups. */
    createPageUrl?: string;
    adminPageLabel?: string;
    /** What one entry is called in the list's actions ("Add new <noun>"). Defaults to adminPageLabel made singular, else the label. */
    noun?: string;
    /** Opens the chosen record (e.g. the referring contact) in a new tab. */
    openUrl?: string | null;
    openTitle?: string;
    /** Unsaved value: the same gold ring as Input's isChanged. */
    isChanged?: boolean;
    /** Room the select keeps on its right for its own clear / caret (px). */
    indicatorSpace?: number;
    children: React.ReactNode;
}

const valueIsSet = (value: unknown): boolean =>
    Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined && value !== "";

/**
 * A react-select / picker dressed like the shared Input: the notched label with
 * its help ⓘ, nothing inside the field. A lookup picker (onRefresh /
 * adminPageUrl) gets "Add new <thing>" and "Reload list" in its open list
 * (option A, 2026-10-05) — injected into the child select.
 */
const SelectField: React.FC<SelectFieldProps> = ({
    label, hasValue, required, tooltip, onRefresh, adminPageUrl, createPageUrl, adminPageLabel, noun, openUrl, openTitle, isChanged, indicatorSpace, children,
}) => {
    const child = React.Children.count(children) === 1 && React.isValidElement(children) ? (children as React.ReactElement<any>) : null;
    const filled = hasValue ?? (child ? valueIsSet(child.props.value) : false);
    const help = tooltip ? <FieldHelp id={child?.props.inputId ?? child?.props.id ?? label} label={label} text={tooltip} /> : null;

    const isLookup = !!(onRefresh || adminPageUrl || createPageUrl);
    // "Add new" only for users who may create in the lookup (Reload stays).
    const canAdd = useCanAddLookup(adminPageUrl);
    const actions: LookupActions | undefined = isLookup
        ? {
            // a multi picker's label is plural ("Languages"); the actions name one entry
            noun: noun ?? (!adminPageLabel && child?.props.isMulti ? singularNoun(lookupNoun(label)) : lookupEntryNoun(label, adminPageLabel)),
            adminPageUrl: canAdd ? adminPageUrl : undefined,
            createPageUrl: canAdd ? createPageUrl : undefined,
            onRefresh,
        }
        : undefined;

    // The child select gets the list actions, and the ⓘ in its placeholder while empty.
    let content: React.ReactNode = children;
    if (child && (isLookup || (help && typeof child.props.placeholder === "string"))) {
        const extra: Record<string, unknown> = isLookup ? lookupSelectProps(actions, child.props) : {};
        if (help && typeof child.props.placeholder === "string") {
            extra.placeholder = <span>{child.props.placeholder}{help}</span>;
        }
        content = React.cloneElement(child, extra);
    }

    const base = indicatorSpace ?? (filled ? 56 : 34);
    const openIcon = !isLookup && openUrl ? (
        <button type="button" className={`btn-unstyled ${classes.selectIcon}`} title={openTitle ?? "Open in a new tab"} aria-label={openTitle ?? "Open in a new tab"} onClick={() => window.open(openUrl, "_blank", "noopener")}>
            <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
        </button>
    ) : null;

    return (
        <div className={`crx-field ${classes.selectField} ${isChanged ? "crx-field--changed" : ""}`} style={{ ["--select-icons" as string]: `${openIcon ? 20 : 0}px` }}>
            {filled && (
                <span className="crx-field-label">
                    {label}
                    {required && <span className="crx-field-label-req" aria-hidden="true"> *</span>}
                    {help}
                </span>
            )}
            {content}
            {openIcon && <span className={classes.selectIcons} style={{ right: base }}>{openIcon}</span>}
        </div>
    );
};

export default SelectField;
